// rounds/public-catalog services
// Pure async functions only — no React, no hooks, no state.
// Parse DTO -> domain (safeParse) at this boundary; the anti-corruption layer.
import { api } from "@core/libs/axios";
import { parseSafe, parseWith } from "@core/parse";
import {
  PublicApplicantSchemaSchema,
  PublicRoundDetailSchema,
  PublicRoundListResponseSchema,
  PublicRoundSummarySchema,
  type PublicRoundCatalog,
  type PublicRoundPreview,
  type PublicRoundSummary,
} from "../types";

/**
 * `GET /acquire/public/intake-rounds/` — the public round catalogue.
 *
 * `api`, not `privateApi`: the endpoint declares `authentication_classes = []`, so there is no
 * token to attach and a 401→refresh interceptor has nothing to do here. Going through the
 * authenticated instance would make an anonymous visitor's catalogue request able to trigger a
 * refresh round-trip on a session that does not exist.
 *
 * No parameters. Filtering (upcoming `SCHEDULED` + live `OPEN`/`CLOSING_SOON`) and ordering
 * (active rounds first by soonest deadline, then upcoming by opening date) are the backend's, and
 * re-sorting client-side would only be a second, disagreeing opinion.
 *
 * Envelope strict, rows lenient — the same split the staff list uses. A missing `server_now` means
 * no deadline on the page can be trusted, so there is nothing safe to render; one malformed round
 * costs that card instead of blanking a page the public sees.
 */
export async function listPublicRounds(): Promise<PublicRoundCatalog> {
  const dto = await api
    .get<unknown>("/acquire/public/intake-rounds/")
    .then((r) => r.data);

  const envelope = parseWith(
    PublicRoundListResponseSchema,
    dto,
    "GET /acquire/public/intake-rounds/"
  );

  return {
    serverNow: envelope.server_now,
    rounds: envelope.intake_rounds
      .map((row) =>
        parseSafe(PublicRoundSummarySchema, row, "rounds.listPublicRounds.row")
      )
      .filter((row): row is PublicRoundSummary => row !== null),
  };
}

/**
 * `GET /acquire/public/intake-rounds/{id}/?locale=` — one publicly visible round in full.
 *
 * Parsed STRICTLY, unlike the catalogue rows. The catalogue can afford to drop a malformed round
 * and still be a useful page; this payload IS the page, and it is what a route guard decides on.
 * Half a round is worse than a clean redirect back to the catalogue.
 *
 * `locale` is sent explicitly because the server localizes eligibility and document copy into it.
 * Callers pass the active interface language; the query key carries the same value.
 *
 * Three failures the caller must expect, all normalized to `ApiError` by the interceptor:
 * - `404` — no such round, or one in a status the public may not see (`DRAFT`, `FROZEN`,
 *   `TRANSFERRED_TO_SELECT`, `ARCHIVED`). Indistinguishable on purpose.
 * - `503 round_configuration_unavailable` — published but its snapshot is momentarily missing;
 *   `details.retryable` is true.
 * - `429` — the shared public throttle (120/min by default).
 */
export async function getPublicRound(
  roundId: string,
  locale: string
): Promise<PublicRoundPreview> {
  const dto = await api
    .get<unknown>(`/acquire/public/intake-rounds/${roundId}/`, {
      params: { locale },
    })
    .then((r) => r.data);

  const round = parseWith(
    PublicRoundDetailSchema,
    dto,
    "GET /acquire/public/intake-rounds/{id}/"
  );

  /**
   * The applicant schema is parsed SEPARATELY and leniently — the same envelope-strict,
   * payload-lenient split the catalogue uses, for the same reason and one more.
   *
   * The reason: this is the one part of the response DRF does not validate. `applicant_schema` is
   * declared `DictField(allow_null=True, required=False)`, so whatever `_localized_schema()` builds
   * is passed straight through. A strict parse would make any change to that function 404 a public
   * page that is otherwise perfectly renderable.
   *
   * The extra reason: it is legitimately absent. A `SCHEDULED` round has real dates and no frozen
   * configuration, so there is nothing to preview — `null` is the correct answer, not a failure.
   *
   * Losing it costs the "complete dossier" section. Losing the round costs the page.
   */
  const schema =
    round.applicant_schema == null
      ? null
      : parseSafe(
          PublicApplicantSchemaSchema,
          round.applicant_schema,
          "rounds.getPublicRound.applicant_schema"
        );

  return { round, schema };
}
